import {
  Box,
  Button,
  HStack,
  Image,
  Spacer,
  Tbody,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import ProfitLossTable from "./reportComponents/profitLossTable";

import { Table } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { fetchProfitLossData } from "../../api/pdfExport";
import MinProfitLossTable from "./reportComponents/minProfitLossTable";
import ReportCalendarComponent from "./reportComponents/reportDatePicker";
import { format } from "date-fns";

const formatDate = (date) => {
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const ProfitLossReport = ({
  customer,
  hideButtons = false,
  externalStartDate,
  externalEndDate,
  externalPrintRef,
}) => {
  const [sectionsData, setSectionsData] = useState({});

  const [triggerRefesh, setTriggerRefresh] = useState(true);

  const now = new Date();

  const year = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();
  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const previousMonthYear = currentMonth === 0 ? year - 1 : year;

  const firstDay = new Date(Date.UTC(previousMonthYear, previousMonth, 1));

  const lastDay = new Date(Date.UTC(year, currentMonth, 0));

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);

  const combinedAccounts = [
    ...(sectionsData?.team || []),
    ...(sectionsData?.marketing || []),
    ...(sectionsData?.operations || []),
  ];

  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: externalPrintRef ? externalPrintRef : printRef,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 20px 60px;  /* Adjust margin */
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
    documentTitle: `Profit & Loss Report ${formatDate(
      externalStartDate ? externalStartDate : startDate
    )}-${formatDate(externalEndDate ? externalEndDate : endDate)}`,
  });

  useEffect(() => {
    const controller = new AbortController();

    const fetchAllProfitLoss = async () => {
      const formattedStartDate = format(
        externalStartDate || startDate,
        "yyyy-MM-dd"
      );
      const formattedEndDate = format(externalEndDate || endDate, "yyyy-MM-dd");

      try {
        const response = await fetchProfitLossData(
          customer.id,
          formattedStartDate,
          formattedEndDate,
          controller.signal
        );
        setSectionsData(response);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAllProfitLoss();

    return () => {
      controller.abort();
    };
  }, [
    customer.id,
    startDate,
    endDate,
    externalStartDate,
    externalEndDate,
    triggerRefesh,
  ]);

  return (
    <Box width={"auto"} overflowX={"auto"} overflowY={"auto"}>
      {!hideButtons && (
        <HStack mb={"20px"}>
          <Button onClick={handlePrint}>Print / Save as PDF</Button>
          <Spacer />
          <ReportCalendarComponent
            callbackFunctionDate={setStartDate}
            date={startDate}
            triggerSetter={setTriggerRefresh}
          ></ReportCalendarComponent>
          <Text color={"white"}>TO</Text>
          <ReportCalendarComponent
            callbackFunctionDate={setEndDate}
            date={endDate}
            triggerSetter={setTriggerRefresh}
          ></ReportCalendarComponent>
        </HStack>
      )}
      <Box ref={externalPrintRef ? externalPrintRef : printRef}>
        <Box
          w="794px"
          bg="white"
          p="40px 60px"
          position="relative"
          css={{
            "@media print": {
              padding: "0 20px !important",
            },
          }}
        >
          <HStack justify="space-between" align="center" mb="20px">
            <Box>
              <Text fontSize="2xl" fontWeight="bold">
                Profit & Loss Report
              </Text>
              <Text fontSize="sm" color="gray.500">
                {customer.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Reporting period from{" "}
                {formatDate(externalStartDate ? externalStartDate : startDate)}{" "}
                - {formatDate(externalEndDate ? externalEndDate : endDate)}
              </Text>
            </Box>
            <Image
              height="60px"
              objectFit="contain"
              src="../lucro-logo-light.png"
              alt="MoreLucro Logo"
            />
          </HStack>

          <Box overflowX="auto" mb={"2px"}>
            <Table variant="unstyled">
              <Tbody>
                <Tr borderY="2px solid grey">
                  <Td></Td>
                  <Td
                    fontSize="14px"
                    textAlign="right"
                    fontWeight="bold"
                    p={"2px 30px"}
                  >
                    Total
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          <ProfitLossTable
            title={"Income"}
            accounts={sectionsData?.income}
            listed={false}
          />
          <ProfitLossTable
            title={"Products"}
            accounts={sectionsData?.cogs}
            listed={false}
          />
          <MinProfitLossTable
            title="Gross Profit"
            account={sectionsData?.totals?.gross_profit}
            listed={false}
          />

          <ProfitLossTable
            title={"Expenses"}
            accounts={combinedAccounts}
            givenTotal={sectionsData?.totals?.total_expense?.total}
            listed={false}
          />
          <MinProfitLossTable
            title="Net Operating Income"
            account={sectionsData?.totals?.net_op_income}
            listed={false}
          />
          <ProfitLossTable
            title={"Other Income"}
            accounts={sectionsData?.otherIncome}
            listed={false}
          />
          <ProfitLossTable
            title={"Other Expense"}
            accounts={sectionsData?.otherExpense}
            listed={false}
          />
          <ProfitLossTable
            title={"Taxes"}
            accounts={sectionsData?.taxes}
            listed={false}
          />
          <MinProfitLossTable
            title="Net Operating Profit"
            account={sectionsData?.totals?.net_op_profit}
            listed={false}
          />
          <Box
            overflowX="auto"
            mt={"2px"}
            borderBottom={"2px solid grey"}
          ></Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfitLossReport;
